import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getCompetitionById } from "../../../services/competitionsServices";
import {
  getCompetitionCompetiteurs,
  updateCompetiteur,
} from "../../../services/competiteursServices";
import { getCompetitionSettingsByCompetitionId } from "../../../services/competitionsettingsServices";
import {
  addDistribution,
  deleteDistribution,
  getCompetitionDistributions,
  updateDistribution,
} from "../../../services/distributionsServices";

import {
  DEFAULT_FILTERS,
  FORMULA_MAX,
  STRICT_EXACT_FORMULAS,
} from "../data/repartitionData";

import {
  enrichCompetiteurs,
  filterRows,
  getFilterOptions,
  isRepechageFormula,
  normalizeDistributions,
  parseDisciplines,
  removeCompetitorFromDistribution,
  shuffleArray,
  sortRows,
} from "../utils/repartitionUtils";

export default function useRepartitionCompetiteursPage({ competitionId }) {
  const [competition, setCompetition] = useState(null);
  const [settings, setSettings] = useState(null);
  const [competiteurs, setCompetiteurs] = useState([]);
  const [distributions, setDistributions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [selectedIds, setSelectedIds] = useState([]);
  const [clearingIds, setClearingIds] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPageState] = useState(100);

  const [sortBy, setSortBy] = useState("default");
  const [sortDir, setSortDir] = useState("asc");

  const [assignForm, setAssignForm] = useState({
    formule_repartition: "",
    numero_tapis: "",
  });

  const country = settings?.cate_country || "France";

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const [competitionData, settingsData, competitorsData, distributionsData] =
        await Promise.all([
          getCompetitionById(competitionId),
          getCompetitionSettingsByCompetitionId(competitionId),
          getCompetitionCompetiteurs(competitionId),
          getCompetitionDistributions(competitionId),
        ]);

      setCompetition(competitionData);
      setSettings(Array.isArray(settingsData) ? settingsData[0] : settingsData);
      setCompetiteurs(Array.isArray(competitorsData) ? competitorsData : []);
      setDistributions(normalizeDistributions(distributionsData || []));
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  const refreshAfterSave = useCallback(async () => {
    try {
      const [competitorsData, distributionsData] = await Promise.all([
        getCompetitionCompetiteurs(competitionId),
        getCompetitionDistributions(competitionId),
      ]);

      setCompetiteurs(Array.isArray(competitorsData) ? competitorsData : []);
      setDistributions(normalizeDistributions(distributionsData || []));
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du rafraîchissement des répartitions.");
    }
  }, [competitionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const disciplinesList = useMemo(
    () => parseDisciplines(competition),
    [competition]
  );

  const enrichedRows = useMemo(
    () =>
      enrichCompetiteurs({
        competiteurs,
        settings,
        disciplinesList,
        distributions,
      }),
    [competiteurs, settings, disciplinesList, distributions]
  );

  const options = useMemo(
    () => getFilterOptions(enrichedRows.filter((row) => row.is_present)),
    [enrichedRows]
  );

  const filteredRows = useMemo(
    () =>
      sortRows({
        rows: filterRows({
          rows: enrichedRows,
          filters,
        }),
        sortBy,
        sortDir,
      }),
    [enrichedRows, filters, sortBy, sortDir]
  );

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * perPage;
  const pageRows = filteredRows.slice(start, start + perPage);

  const visibleIds = useMemo(() => pageRows.map((row) => row.id), [pageRows]);

  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const setPerPage = useCallback((value) => {
    setPerPageState(value);
    setPage(1);
  }, []);

  const toggleSelect = useCallback((competitorId) => {
    setSelectedIds((current) =>
      current.includes(competitorId)
        ? current.filter((id) => id !== competitorId)
        : [...current, competitorId]
    );
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((current) => {
      const currentSet = new Set(current);

      if (visibleIds.every((id) => currentSet.has(id))) {
        return current.filter((id) => !visibleIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleIds]));
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const toggleSortDist = useCallback(() => {
    if (sortBy !== "dist") {
      setSortBy("dist");
      setSortDir("asc");
      return;
    }

    if (sortDir === "asc") {
      setSortDir("desc");
      return;
    }

    setSortBy("default");
    setSortDir("asc");
  }, [sortBy, sortDir]);

  const detachFromExistingDistributions = useCallback(
    async (movingIds) => {
      const movingSet = new Set(movingIds.map((id) => Number(id)));

      const distributionsToUpdate = distributions
        .map((distribution) => {
          const ids = Array.isArray(distribution.competiteurs)
            ? distribution.competiteurs
            : [];

          const hasMovingCompetitor = ids.some((id) =>
            movingSet.has(Number(id))
          );

          if (!hasMovingCompetitor) return null;

          const next = {
            ...distribution,
            competiteurs: ids.filter((id) => !movingSet.has(Number(id))),
          };

          next.position_aleatoire = next.competiteurs.map((_, index) => index + 1);

          return next;
        })
        .filter(Boolean);

      await Promise.all(
        distributionsToUpdate.map((distribution) =>
          updateDistribution({
            id: distribution.id,
            competiteurs: distribution.competiteurs,
            position_aleatoire: distribution.position_aleatoire,
          })
        )
      );
    },
    [distributions]
  );

  const createDistribution = useCallback(async () => {
    const { formule_repartition, numero_tapis } = assignForm;

    if (!formule_repartition) {
      toast.error("Choisis une formule.");
      return;
    }

    if (!selectedIds.length) {
      toast.error("Sélectionne au moins un compétiteur.");
      return;
    }

    const isNC = formule_repartition === "non_classes";
    const isSolo = formule_repartition === "seul_dans_distribution";
    const isRepechage = isRepechageFormula(formule_repartition);

    if (!isNC && !numero_tapis) {
      toast.error("Choisis un numéro de tapis.");
      return;
    }

    const cap = FORMULA_MAX[formule_repartition];

    if (!isNC && cap && selectedIds.length > cap) {
      toast.error(`La formule choisie limite à ${cap} compétiteur(s).`);
      return;
    }

    if (
      STRICT_EXACT_FORMULAS.includes(formule_repartition) &&
      selectedIds.length !== FORMULA_MAX[formule_repartition]
    ) {
      toast.error(
        `Pour cette formule, il faut exactement ${
          FORMULA_MAX[formule_repartition]
        } compétiteur(s).`
      );
      return;
    }

    const selected = enrichedRows.filter((competitor) =>
      selectedIds.includes(competitor.id)
    );

    if (!selected.length) {
      toast.error("Sélection invalide.");
      return;
    }

    const first = selected[0];

    const sameDiscipline = selected.every(
      (competitor) => competitor._discipline === first._discipline
    );

    if (!sameDiscipline) {
      toast.error(
        "Tous les compétiteurs sélectionnés doivent être de la même discipline."
      );
      return;
    }

    try {
      const randomizedSelected = shuffleArray(selected);
      const randomizedIds = randomizedSelected.map((competitor) => competitor.id);
      const randomizedPositions = randomizedSelected.map((_, index) => index + 1);

      await detachFromExistingDistributions(randomizedIds);

      const numeroTapisValue = isNC ? 0 : Number(numero_tapis);

      const distribution = await addDistribution({
        numero_tapis: numeroTapisValue,
        formule_repartition,
        genre: first.genre_competiteur,
        categorie_age: first._ageCat,
        categorie_poids: first._weightCat,
        discipline: first._discipline,
        competiteurs: randomizedIds,
        position_aleatoire: randomizedPositions,
        competition_id: competitionId,
        is_completed: isSolo || isNC ? 1 : 0,
      });

      await Promise.all(
        randomizedSelected.map((competitor) =>
          updateCompetiteur(competitor.id, {
            is_distributed: true,
            distribution_id: distribution.id,
            ...(isSolo
              ? {
                  classement_position: 1,
                  total_victoires: 0,
                  total_points: 0,
                  is_disqualified: 0,
                }
              : {}),
            ...(isNC
              ? {
                  classement_position: 0,
                  total_victoires: 0,
                  total_points: 0,
                  is_disqualified: 0,
                }
              : {}),
          })
        )
      );

      toast.success(
        isNC
          ? "Distribution NC créée ✅"
          : isRepechage
          ? "Tableau avec repêchage créé ✅"
          : "Distribution créée avec ordre aléatoire ✅"
      );

      clearSelection();

      setAssignForm((current) => ({
        ...current,
        formule_repartition: "",
        numero_tapis: isNC ? "" : current.numero_tapis,
      }));

      await refreshAfterSave();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la création de la distribution.");
    }
  }, [
    assignForm,
    selectedIds,
    enrichedRows,
    competitionId,
    detachFromExistingDistributions,
    clearSelection,
    refreshAfterSave,
  ]);

  const deleteExistingDistribution = useCallback(
    async (distribution) => {
      if (!distribution) return false;

      if (!window.confirm("Supprimer cette distribution ?")) return false;

      try {
        const competitorIds = Array.isArray(distribution.competiteurs)
          ? distribution.competiteurs
          : [];

        const formula = String(distribution.formule_repartition || "");
        const isSolo = formula === "seul_dans_distribution";

        await Promise.all(
          competitorIds.map((competitorId) =>
            updateCompetiteur(competitorId, {
              is_distributed: false,
              distribution_id: null,
              ...(isSolo ? { classement_position: 0 } : {}),
            })
          )
        );

        await deleteDistribution(distribution.id);

        toast.success("Distribution supprimée.");
        await refreshAfterSave();
        return true;
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la suppression.");
        return false;
      }
    },
    [refreshAfterSave]
  );

  const clearCompetiteur = useCallback(
    async (row) => {
      const competitorId = row?.id;
      if (!competitorId) return;

      const label = `${row?.nom_competiteur ?? ""} ${
        row?.prenom_competiteur ?? ""
      }`.trim();

      if (
        !window.confirm(
          `Réinitialiser résultats et distribution pour ${
            label || "ce compétiteur"
          } ?`
        )
      ) {
        return;
      }

      const payload = {
        is_distributed: 0,
        distribution_id: null,
        classement_position: 0,
        total_victoires: 0,
        total_points: 0,
        is_disqualified: 0,
      };

      setClearingIds((current) => Array.from(new Set([...current, competitorId])));

      try {
        const currentDistribution = distributions.find(
          (distribution) => String(distribution.id) === String(row._distId)
        );

        if (currentDistribution) {
          const cleaned = removeCompetitorFromDistribution(
            currentDistribution,
            competitorId
          );

          await updateDistribution({
            id: cleaned.id,
            competiteurs: cleaned.competiteurs,
            position_aleatoire: cleaned.position_aleatoire,
          });
        }

        await updateCompetiteur(competitorId, payload);

        toast.success("Compétiteur réinitialisé.");
        await refreshAfterSave();
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de la réinitialisation du compétiteur.");
      } finally {
        setClearingIds((current) =>
          current.filter((id) => id !== competitorId)
        );
      }
    },
    [distributions, refreshAfterSave]
  );

  return {
    competition,
    settings,
    country,
    loading,

    distributions,
    competiteurs,
    enrichedRows,
    filteredRows,
    pageRows,

    filters,
    updateFilter,
    resetFilters,
    options,
    disciplinesList,

    selectedIds,
    selectedCount: selectedIds.length,
    visibleIds,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
    clearSelection,

    page: currentPage,
    perPage,
    total,
    totalPages,
    setPage,
    setPerPage,

    sortBy,
    sortDir,
    toggleSortDist,

    assignForm,
    setAssignForm,

    clearingIds,

    actions: {
      refresh,
      refreshAfterSave,
      createDistribution,
      deleteExistingDistribution,
      clearCompetiteur,
    },
  };
}